import { useRef } from "react";
import { Plus, Trash2, Upload, Download, BookUser } from "lucide-react";
import { emptyLineItem } from "@/lib/invoiceUtils";

const labelCls =
  "text-[10px] font-bold uppercase tracking-[0.18em] text-[#52525B] mb-1.5 block";
const inputCls =
  "w-full px-3 py-2.5 text-sm font-medium bg-white border border-[#E5E5E5] rounded-none outline-none transition-all focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A] placeholder:text-[#A1A1AA]";
const textareaCls =
  "w-full px-3 py-2.5 text-sm font-medium bg-white border border-[#E5E5E5] rounded-none outline-none transition-all focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A] placeholder:text-[#A1A1AA] resize-none";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function SectionHeader({ index, title }) {
  return (
    <div className="flex items-end justify-between border-b border-[#0A0A0A] pb-2 mb-5">
      <h2 className="text-lg font-extrabold tracking-tight">{title}</h2>
      <span className="font-mono text-[10px] tracking-widest text-[#A1A1AA]">
        {index}
      </span>
    </div>
  );
}

export default function ControlPanel({
  business,
  customer,
  setCustomer,
  shipping,
  setShipping,
  eway,
  setEway,
  meta,
  setMeta,
  items,
  setItems,
  taxRate,
  setTaxRate,
  taxLabel,
  setTaxLabel,
  parties = [],
  onSelectParty,
  onSaveParty,
  onRemoveParty,
  onImportParties,
  onExportParties,
  lastInvoiceNumber,
}) {
  const partiesFileInputRef = useRef(null);

  const updateItem = (id, key, value) => {
    setItems(
      items.map((it) =>
        it.id === id
          ? {
              ...it,
              [key]:
                key === "qty" || key === "rate"
                  ? value === ""
                    ? ""
                    : Number(value)
                  : value,
            }
          : it,
      ),
    );
  };

  const addItem = () =>
    setItems([
      ...items,
      { ...emptyLineItem(), unit: business?.defaultUnit || "PCS" },
    ]);
  const removeItem = (id) => {
    if (items.length === 1) {
      setItems([{ ...emptyLineItem(), unit: business?.defaultUnit || "PCS" }]);
      return;
    }
    setItems(items.filter((it) => it.id !== id));
  };

  return (
    <div className="px-6 lg:px-10 py-8 space-y-12">
      {/* COMPANY PREVIEW (read-only — edit via Setup) */}
      <section data-testid="company-preview-section">
        <SectionHeader index="01" title="Company · Read-only" />
        <div className="flex items-start gap-4 border border-[#E5E5E5] bg-[#FAFAFA] p-4">
          {business.logoDataUrl ? (
            <img
              src={business.logoDataUrl}
              alt="logo"
              data-testid="company-logo-preview"
              className="w-16 h-16 object-contain border border-[#E5E5E5] p-1 bg-white shrink-0"
            />
          ) : (
            <div className="w-16 h-16 border border-dashed border-[#A1A1AA] flex items-center justify-center text-[9px] uppercase tracking-widest text-[#A1A1AA] shrink-0">
              No Logo
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div
              data-testid="company-name-display"
              className="text-base font-extrabold tracking-tight truncate"
            >
              {business.name || "Set up your company"}
            </div>
            {business.tagline && (
              <div className="text-[11px] italic text-[#52525B] mt-0.5 truncate">
                {business.tagline}
              </div>
            )}
            {business.gstin && (
              <div className="text-[10px] font-mono text-[#0A0A0A] mt-1">
                GSTIN: {business.gstin}
              </div>
            )}
            <div className="text-[10px] text-[#52525B] mt-0.5 flex flex-wrap gap-x-2">
              {business.phone && <span>T · {business.phone}</span>}
              {business.email && <span>E · {business.email}</span>}
            </div>
          </div>
        </div>
        <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
          Click <span className="font-bold">Setup</span> in the header to edit
          company details, tagline, payment details, notes & terms.
        </p>
      </section>

      {/* INVOICE META */}
      <section data-testid="invoice-meta-section">
        <SectionHeader index="02" title="Invoice Details" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Invoice #">
            <input
              data-testid="invoice-number-input"
              className={inputCls}
              value={meta.number}
              onChange={(e) => setMeta({ ...meta, number: e.target.value })}
            />
            {lastInvoiceNumber && (
              <div
                data-testid="last-invoice-number"
                className="mt-1 text-[10px] font-mono text-[#52525B]"
              >
                Last: <span className="font-bold">{lastInvoiceNumber}</span>
              </div>
            )}
          </Field>
          <Field label="Invoice Date">
            <input
              type="date"
              data-testid="invoice-date-input"
              className={inputCls}
              value={meta.date}
              onChange={(e) => setMeta({ ...meta, date: e.target.value })}
            />
          </Field>
          <Field label="Due Date">
            <input
              type="date"
              data-testid="invoice-due-date-input"
              className={inputCls}
              value={meta.dueDate}
              onChange={(e) => setMeta({ ...meta, dueDate: e.target.value })}
            />
          </Field>
        </div>
      </section>

      {/* CUSTOMER */}
      <section data-testid="customer-section">
        <SectionHeader index="03" title="Bill To · Customer" />

        {/* Parties book bar */}
        <div
          className="mb-5 border border-[#0A0A0A] bg-[#FAFAFA] p-3 flex flex-wrap items-center gap-2"
          data-testid="parties-bar"
        >
          <div className="flex items-center gap-2 mr-1">
            <BookUser size={14} strokeWidth={2.5} />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em]">
              Parties Book
            </span>
            <span
              className="text-[10px] font-mono text-[#52525B]"
              data-testid="parties-count"
            >
              [{parties.length}]
            </span>
          </div>

          <select
            data-testid="parties-select"
            className="flex-1 min-w-[180px] px-3 py-2 text-xs font-medium bg-white border border-[#0A0A0A] rounded-none outline-none focus:ring-2 focus:ring-[#0A0A0A]"
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (v === "") return;
              if (v.startsWith("remove:")) {
                const idx = Number(v.slice(7));
                onRemoveParty?.(idx);
              } else {
                onSelectParty?.(Number(v));
              }
              e.target.value = "";
            }}
          >
            <option value="">— Select party to load —</option>
            {parties.length === 0 && (
              <option value="" disabled>
                (Parties book is empty)
              </option>
            )}
            {parties.map((p, i) => (
              <option key={`p-${i}`} value={i}>
                {p.name || "(unnamed)"}
                {p.billTo?.gstin ? ` · ${p.billTo.gstin}` : ""}
                {p.shipTo ? " · ⇉ ship-to" : ""}
              </option>
            ))}
            {parties.length > 0 && (
              <optgroup label="Remove from book">
                {parties.map((p, i) => (
                  <option key={`rm-${i}`} value={`remove:${i}`}>
                    Remove · {p.name || "(unnamed)"}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          <button
            type="button"
            onClick={onSaveParty}
            data-testid="save-party-btn"
            title="Save current Bill To (and Ship To) as a party"
            className="inline-flex items-center gap-1.5 border border-[#0A0A0A] bg-white px-2.5 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            <Plus size={12} strokeWidth={2.5} />
            Save
          </button>

          <label
            data-testid="import-parties-btn"
            title="Import parties.json"
            className="inline-flex items-center gap-1.5 border border-[#0A0A0A] bg-white px-2.5 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-white transition-colors cursor-pointer"
          >
            <Upload size={12} strokeWidth={2.5} />
            Import
            <input
              ref={partiesFileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              data-testid="import-parties-input"
              onChange={(e) => {
                onImportParties?.(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>

          <button
            type="button"
            onClick={onExportParties}
            data-testid="export-parties-btn"
            title="Export parties.json"
            className="inline-flex items-center gap-1.5 border border-[#0A0A0A] bg-white px-2.5 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            <Download size={12} strokeWidth={2.5} />
            Export
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Customer Name">
            <input
              data-testid="customer-name-input"
              className={inputCls}
              placeholder="Rahul Mehta"
              value={customer.name}
              onChange={(e) =>
                setCustomer({ ...customer, name: e.target.value })
              }
            />
          </Field>
          <Field label="GSTIN (optional)">
            <input
              data-testid="customer-gstin-input"
              className={inputCls}
              placeholder="29ZZZZZ9999Z9Z9"
              value={customer.gstin}
              onChange={(e) =>
                setCustomer({ ...customer, gstin: e.target.value })
              }
            />
          </Field>
          <Field label="Phone">
            <input
              data-testid="customer-phone-input"
              className={inputCls}
              placeholder="+91 90000 00000"
              value={customer.phone}
              onChange={(e) =>
                setCustomer({ ...customer, phone: e.target.value })
              }
            />
          </Field>
          <Field label="Address">
            <textarea
              data-testid="customer-address-input"
              rows={2}
              className={textareaCls}
              placeholder="Customer billing address"
              value={customer.address}
              onChange={(e) =>
                setCustomer({ ...customer, address: e.target.value })
              }
            />
          </Field>
        </div>
      </section>

      {/* SHIP TO */}
      <section data-testid="shipping-section">
        <SectionHeader index="03b" title="Ship To · Delivery" />
        <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="shipping-include-toggle"
            className="w-4 h-4 accent-[#0A0A0A]"
            checked={shipping.enabled}
            onChange={(e) =>
              setShipping({ ...shipping, enabled: e.target.checked })
            }
          />
          <span className="text-xs font-bold uppercase tracking-widest">
            Include Ship To details on invoice
          </span>
        </label>
        {shipping.enabled && (
          <>
            <label className="flex items-center gap-2 mb-4 ml-6 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="shipping-same-toggle"
                className="w-4 h-4 accent-[#0A0A0A]"
                checked={shipping.sameAsBilling}
                onChange={(e) =>
                  setShipping({ ...shipping, sameAsBilling: e.target.checked })
                }
              />
              <span className="text-xs font-bold uppercase tracking-widest">
                Same as Bill To
              </span>
            </label>
            {!shipping.sameAsBilling && (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                data-testid="shipping-fields"
              >
                <Field label="Consignee Name">
                  <input
                    data-testid="shipping-name-input"
                    className={inputCls}
                    placeholder="Receiver name"
                    value={shipping.name}
                    onChange={(e) =>
                      setShipping({ ...shipping, name: e.target.value })
                    }
                  />
                </Field>
                <Field label="GSTIN (optional)">
                  <input
                    data-testid="shipping-gstin-input"
                    className={inputCls}
                    placeholder="State-prefixed GSTIN"
                    value={shipping.gstin}
                    onChange={(e) =>
                      setShipping({ ...shipping, gstin: e.target.value })
                    }
                  />
                </Field>
                <Field label="Phone">
                  <input
                    data-testid="shipping-phone-input"
                    className={inputCls}
                    placeholder="+91 ..."
                    value={shipping.phone}
                    onChange={(e) =>
                      setShipping({ ...shipping, phone: e.target.value })
                    }
                  />
                </Field>
                <Field label="Shipping Address">
                  <textarea
                    data-testid="shipping-address-input"
                    rows={2}
                    className={textareaCls}
                    placeholder="Delivery / consignee address"
                    value={shipping.address}
                    onChange={(e) =>
                      setShipping({ ...shipping, address: e.target.value })
                    }
                  />
                </Field>
              </div>
            )}
          </>
        )}
      </section>

      {/* E-WAY BILL */}
      <section data-testid="eway-section">
        <SectionHeader index="03c" title="E-Way Bill · Transport" />
        <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="eway-toggle"
            className="w-4 h-4 accent-[#0A0A0A]"
            checked={eway.enabled}
            onChange={(e) => setEway({ ...eway, enabled: e.target.checked })}
          />
          <span className="text-xs font-bold uppercase tracking-widest">
            Include e-way bill details on invoice
          </span>
        </label>
        {eway.enabled && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            data-testid="eway-fields"
          >
            <Field label="E-Way Bill No.">
              <input
                data-testid="eway-number-input"
                className={inputCls}
                placeholder="123456789012"
                value={eway.number}
                onChange={(e) =>
                  setEway({ ...eway, number: e.target.value })
                }
              />
            </Field>
            <Field label="E-Way Bill Date">
              <input
                type="date"
                data-testid="eway-date-input"
                className={inputCls}
                value={eway.date}
                onChange={(e) => setEway({ ...eway, date: e.target.value })}
              />
            </Field>
            <Field label="Vehicle No.">
              <input
                data-testid="eway-vehicle-input"
                className={inputCls}
                placeholder="KA01AB1234"
                value={eway.vehicle}
                onChange={(e) =>
                  setEway({ ...eway, vehicle: e.target.value })
                }
              />
            </Field>
            <Field label="Transporter Name">
              <input
                data-testid="eway-transporter-input"
                className={inputCls}
                placeholder="Transporter / courier"
                value={eway.transporter}
                onChange={(e) =>
                  setEway({ ...eway, transporter: e.target.value })
                }
              />
            </Field>
            <Field label="Place of Supply" className="sm:col-span-2">
              <input
                data-testid="eway-place-input"
                className={inputCls}
                placeholder="State / city of supply"
                value={eway.placeOfSupply}
                onChange={(e) =>
                  setEway({ ...eway, placeOfSupply: e.target.value })
                }
              />
            </Field>
          </div>
        )}
      </section>

      {/* LINE ITEMS */}
      <section data-testid="line-items-section">
        <SectionHeader index="04" title="Line Items" />

        <div className="hidden md:grid grid-cols-12 gap-2 mb-2 px-1">
          <span className={`${labelCls} col-span-2 mb-0`}>Item</span>
          <span className={`${labelCls} col-span-3 mb-0`}>Description</span>
          <span className={`${labelCls} col-span-2 mb-0`}>HSN</span>
          <span className={`${labelCls} col-span-1 mb-0 text-right`}>Qty</span>
          <span className={`${labelCls} col-span-1 mb-0`}>Unit</span>
          <span className={`${labelCls} col-span-2 mb-0 text-right`}>Rate</span>
          <span className={`${labelCls} col-span-1 mb-0`}></span>
        </div>

        <div className="space-y-3">
          {items.map((it, idx) => (
            <div
              key={it.id}
              data-testid={`line-item-row-${idx}`}
              className="grid grid-cols-12 gap-2 items-start border-l-2 border-[#0A0A0A] pl-3"
            >
              <div className="col-span-6 md:col-span-2 editor-cell">
                <span className="md:hidden text-[9px] uppercase tracking-widest font-bold text-[#52525B] block mb-1">
                  Item
                </span>
                <input
                  data-testid={`item-name-${idx}`}
                  value={it.name}
                  onChange={(e) => updateItem(it.id, "name", e.target.value)}
                  placeholder="Product / Service"
                />
              </div>
              <div className="col-span-12 md:col-span-3 editor-cell">
                <span className="md:hidden text-[9px] uppercase tracking-widest font-bold text-[#52525B] block mb-1">
                  Description
                </span>
                <input
                  data-testid={`item-description-${idx}`}
                  value={it.description}
                  onChange={(e) =>
                    updateItem(it.id, "description", e.target.value)
                  }
                  placeholder="Notes"
                />
              </div>
              <div className="col-span-6 md:col-span-2 editor-cell">
                <span className="md:hidden text-[9px] uppercase tracking-widest font-bold text-[#52525B] block mb-1">
                  HSN
                </span>
                <input
                  data-testid={`item-hsn-${idx}`}
                  value={it.hsn}
                  onChange={(e) => updateItem(it.id, "hsn", e.target.value)}
                  placeholder="0000"
                />
              </div>
              <div className="col-span-3 md:col-span-1 editor-cell">
                <span className="md:hidden text-[9px] uppercase tracking-widest font-bold text-[#52525B] block mb-1">
                  Qty
                </span>
                <input
                  data-testid={`item-qty-${idx}`}
                  type="number"
                  min="0"
                  step="any"
                  className="text-right"
                  value={it.qty}
                  onChange={(e) => updateItem(it.id, "qty", e.target.value)}
                />
              </div>
              <div className="col-span-3 md:col-span-1 editor-cell">
                <span className="md:hidden text-[9px] uppercase tracking-widest font-bold text-[#52525B] block mb-1">
                  Unit
                </span>
                <input
                  data-testid={`item-unit-${idx}`}
                  value={it.unit ?? ""}
                  onChange={(e) => updateItem(it.id, "unit", e.target.value)}
                  placeholder="PCS"
                />
              </div>
              <div className="col-span-6 md:col-span-2 editor-cell">
                <span className="md:hidden text-[9px] uppercase tracking-widest font-bold text-[#52525B] block mb-1">
                  Rate
                </span>
                <input
                  data-testid={`item-rate-${idx}`}
                  type="number"
                  min="0"
                  step="any"
                  className="text-right"
                  value={it.rate}
                  onChange={(e) => updateItem(it.id, "rate", e.target.value)}
                />
              </div>
              <div className="col-span-3 md:col-span-1 flex justify-end items-start pt-2">
                <button
                  type="button"
                  onClick={() => removeItem(it.id)}
                  data-testid={`item-remove-${idx}`}
                  aria-label="Remove item"
                  className="w-9 h-9 flex items-center justify-center border border-[#E5E5E5] hover:border-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-colors"
                >
                  <Trash2 size={14} strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addItem}
          data-testid="add-line-item-btn"
          className="mt-4 inline-flex items-center gap-2 border border-[#0A0A0A] bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
        >
          <Plus size={14} strokeWidth={2.5} />
          Add Line Item
        </button>
      </section>

      {/* TAX */}
      <section data-testid="tax-notes-section">
        <SectionHeader index="05" title="Tax" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Tax Label">
            <input
              data-testid="tax-label-input"
              className={inputCls}
              placeholder="GST / VAT"
              value={taxLabel}
              onChange={(e) => setTaxLabel(e.target.value)}
            />
          </Field>
          <Field label="Tax %">
            <input
              data-testid="tax-rate-input"
              type="number"
              min="0"
              max="100"
              step="any"
              className={inputCls}
              value={taxRate}
              onChange={(e) =>
                setTaxRate(e.target.value === "" ? 0 : Number(e.target.value))
              }
            />
          </Field>
        </div>
        <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
          Default Notes, Payment Details, and Terms are managed in{" "}
          <span className="font-bold">Setup</span> and appear on the invoice
          when filled.
        </p>
      </section>
    </div>
  );
}
